import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { AuthService } from './auth-guard/auth.service';

interface PresenceTabRecord {
  id: string;
  ts: number;
}

/**
 * Keeps the driver Live in FH Progress while any portal tab is open.
 * Sends an immediate Offline ping when the last tab or the browser closes.
 */
@Injectable({
  providedIn: 'root',
})
export class DriverPresenceService {
  private static readonly HEARTBEAT_MS = 30_000;
  private static readonly TAB_TTL_MS = 45_000;
  private static readonly CHANNEL_NAME = 'saferwise-driver-presence';

  private readonly tabId = DriverPresenceService.newTabId();
  private readonly tabsStorageKey = `saferwise-driver:${environment.name}:presence-tabs`;
  private readonly boundPageHide = (event: PageTransitionEvent) => this.onPageHide(event);
  private readonly boundPageShow = (event: PageTransitionEvent) => this.onPageShow(event);
  private readonly boundBeforeUnload = () => this.onLastTabUnload();

  private heartbeatHandle: ReturnType<typeof setInterval> | null = null;
  private channel: BroadcastChannel | null = null;
  private peerIds = new Set<string>();
  private started = false;
  private unloadSent = false;

  constructor(private authService: AuthService) {
    if (typeof window === 'undefined') {
      return;
    }
    window.addEventListener('pagehide', this.boundPageHide);
    window.addEventListener('pageshow', this.boundPageShow);
    window.addEventListener('beforeunload', this.boundBeforeUnload);
    if (this.authService.LoginStatus && this.authService.getToken()) {
      this.start();
    }
  }

  start(): void {
    if (!this.authService.getToken()) {
      return;
    }
    this.unloadSent = false;
    this.started = true;
    this.openChannel();
    this.registerTab();
    this.stopHeartbeatTimer();
    this.send('heartbeat');
    this.heartbeatHandle = setInterval(() => {
      if (!this.authService.getToken()) {
        this.stopLocal();
        return;
      }
      this.registerTab();
      this.send('heartbeat');
    }, DriverPresenceService.HEARTBEAT_MS);
  }

  /** Explicit logout: Offline + revoke JWT, then clear local session. */
  signOut(): void {
    this.send('logout', true);
    this.stopLocal();
    this.authService.logout();
  }

  /** Stop heartbeats without an Offline ping (already unauthorized / revoked). */
  stopLocal(): void {
    this.started = false;
    this.stopHeartbeatTimer();
    this.unregisterTab();
    this.closeChannel();
    this.peerIds.clear();
  }

  private onPageHide(event: PageTransitionEvent): void {
    if (event.persisted) {
      return;
    }
    this.onLastTabUnload();
  }

  private onPageShow(event: PageTransitionEvent): void {
    if (event.persisted && this.authService.LoginStatus && this.authService.getToken()) {
      this.unloadSent = false;
      this.start();
    }
  }

  private onLastTabUnload(): void {
    if (!this.started || this.unloadSent) {
      return;
    }
    this.unloadSent = true;
    this.announceBye();
    this.unregisterTab();
    this.sendOfflineBeacon();
    this.stopHeartbeatTimer();
  }

  private send(path: 'heartbeat' | 'offline' | 'logout', keepalive = false): void {
    const token = this.authService.getToken();
    if (!token) {
      return;
    }
    const base = (environment.apiBaseUrl ?? '').replace(/\/+$/, '');
    const url = `${base}/v1/driver/presence/${path}`;
    void fetch(url, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      keepalive,
    }).catch(() => {
      // Presence pings must never log the driver out.
    });
  }

  /**
   * Tab close cannot complete a CORS preflight with Authorization.
   * sendBeacon is a simple POST (query jti) that browsers actually deliver on unload.
   */
  private sendOfflineBeacon(): void {
    const token = this.authService.getToken();
    if (!token) {
      return;
    }
    const jti = DriverPresenceService.extractJti(token);
    const base = (environment.apiBaseUrl ?? '').replace(/\/+$/, '');
    if (jti && typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const url = `${base}/v1/auth/presence/offline?jti=${encodeURIComponent(jti)}`;
      const sent = navigator.sendBeacon(url);
      if (sent) {
        return;
      }
    }
    this.send('offline', true);
  }

  private static extractJti(token: string): string | null {
    try {
      const payload = token.split('.')[1];
      if (!payload) {
        return null;
      }
      const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as { jti?: string };
      const jti = (json.jti ?? '').trim();
      return jti || null;
    } catch {
      return null;
    }
  }

  private registerTab(): void {
    const tabs = this.readLiveTabs().filter((tab) => tab.id !== this.tabId);
    tabs.push({ id: this.tabId, ts: Date.now() });
    this.writeTabs(tabs);
  }

  private unregisterTab(): number {
    const tabs = this.readLiveTabs().filter((tab) => tab.id !== this.tabId);
    this.writeTabs(tabs);
    return tabs.length;
  }

  private readLiveTabs(): PresenceTabRecord[] {
    try {
      const raw = localStorage.getItem(this.tabsStorageKey);
      if (!raw) {
        return [];
      }
      const parsed = JSON.parse(raw) as PresenceTabRecord[];
      if (!Array.isArray(parsed)) {
        return [];
      }
      const cutoff = Date.now() - DriverPresenceService.TAB_TTL_MS;
      return parsed.filter((tab) => !!tab?.id && typeof tab.ts === 'number' && tab.ts >= cutoff);
    } catch {
      return [];
    }
  }

  private writeTabs(tabs: PresenceTabRecord[]): void {
    try {
      localStorage.setItem(this.tabsStorageKey, JSON.stringify(tabs));
    } catch {
      // ignore quota / private-mode failures
    }
  }

  private openChannel(): void {
    this.closeChannel();
    if (typeof BroadcastChannel === 'undefined') {
      return;
    }
    try {
      this.channel = new BroadcastChannel(DriverPresenceService.CHANNEL_NAME);
      this.channel.onmessage = (event: MessageEvent) => {
        const data = event.data as { type?: string; id?: string } | null;
        if (!data?.id || data.id === this.tabId) {
          return;
        }
        if (data.type === 'hello') {
          this.peerIds.add(data.id);
          this.channel?.postMessage({ type: 'ack', id: this.tabId });
        } else if (data.type === 'ack') {
          this.peerIds.add(data.id);
        } else if (data.type === 'bye') {
          this.peerIds.delete(data.id);
          this.send('heartbeat');
        }
      };
      this.channel.postMessage({ type: 'hello', id: this.tabId });
    } catch {
      this.channel = null;
    }
  }

  private announceBye(): void {
    try {
      this.channel?.postMessage({ type: 'bye', id: this.tabId });
    } catch {
      // ignore
    }
  }

  private closeChannel(): void {
    if (!this.channel) {
      return;
    }
    try {
      this.channel.close();
    } catch {
      // ignore
    }
    this.channel = null;
  }

  private stopHeartbeatTimer(): void {
    if (this.heartbeatHandle != null) {
      clearInterval(this.heartbeatHandle);
      this.heartbeatHandle = null;
    }
  }

  private static newTabId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return `tab-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}
